import { invoiceOutstandingMinor } from "../../shared/invoice-settlement";
import { useRef, useState, type RefObject } from "react";
import { useSession } from "../auth/session";
import { Button } from "../components/Button";
import { Field } from "../components/FormControls";
import { Modal } from "../components/Modal";
import { RequestNotice } from "../components/RequestNotice";
import { SUBSCRIPTION_REQUIRED_MESSAGE } from "../subscriptions/access";
import { formatMoney, invoiceRequest, type InvoiceDraftRecord } from "./invoiceApi";

export function paymentEligibility(invoice: InvoiceDraftRecord): string | null {
  if (invoice.documentState !== "issued") return "Draft invoices cannot receive payments.";
  if (invoice.archivedAt) return "Archived invoices cannot receive payments.";
  if (invoiceOutstandingMinor(invoice) <= 0) return "Paid invoices cannot receive another payment.";
  return null;
}

function moneyInput(value: string): number | null {
  if (!/^\d{1,8}(?:\.\d{1,2})?$/.test(value)) return null;
  const [whole, fraction = ""] = value.split(".");
  return Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
}

function displayDate(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-US", { day: "numeric", month: "short", timeZone: "UTC", year: "numeric" }).format(new Date(`${value}T00:00:00Z`));
}

export function BatchPaymentModal({ invoices, onClose, onSuccess, restoreFocusRef }: {
  invoices: InvoiceDraftRecord[];
  onClose(): void;
  onSuccess(count: number): void;
  restoreFocusRef: RefObject<HTMLElement | null>;
}) {
  const { getToken } = useSession();
  const tokenRef = useRef(getToken); tokenRef.current = getToken;
  const keyRef = useRef(crypto.randomUUID());
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const eligible = invoices.filter((invoice) => !paymentEligibility(invoice));
  const excluded = invoices.filter((invoice) => paymentEligibility(invoice));

  async function submit() {
    const nextErrors: Record<string, string> = {};
    const allocations = eligible.map((invoice) => {
      const amountMinor = moneyInput((amounts[invoice.id] ?? "").trim());
      if (amountMinor === null || amountMinor <= 0) nextErrors[invoice.id] = "Enter an amount greater than zero.";
      else if (amountMinor > invoiceOutstandingMinor(invoice)) nextErrors[invoice.id] = "Amount exceeds the outstanding balance.";
      return { id: invoice.id, version: invoice.version, amountMinor: amountMinor ?? 0 };
    });
    const validDate = /^\d{4}-\d{2}-\d{2}$/.test(paidOn) && !Number.isNaN(Date.parse(paidOn))
      && new Date(paidOn).toISOString().slice(0, 10) === paidOn && paidOn <= new Date().toISOString().slice(0, 10);
    if (!validDate) nextErrors.paidOn = "Enter a payment date that is not in the future.";
    if (!reference.trim() || reference.length > 200) nextErrors.reference = "Enter a payment reference of 200 characters or fewer.";
    setErrors(nextErrors);
    if (!eligible.length || Object.keys(nextErrors).length) return;
    setBusy(true); setError("");
    try {
      const result = await invoiceRequest<{ recordedCount: number }>(tokenRef.current, "/api/invoices/batch-payment", {
        method: "POST", body: JSON.stringify({ key: keyRef.current, paidOn, reference: reference.trim(), allocations }),
      });
      onSuccess(result.recordedCount);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "The payments could not be recorded.");
    } finally { setBusy(false); }
  }

  return <Modal open label="Record payments" onClose={() => { if (!busy) onClose(); }} restoreFocusRef={restoreFocusRef}>
    <div className="batch-payment-form">
      <p>Enter a separate amount for each eligible invoice. No amount is allocated automatically.</p>
      {excluded.length ? <div className="batch-payment-excluded"><p>{excluded.length} selected invoice{excluded.length === 1 ? " is" : "s are"} excluded:</p><ul>{excluded.map((invoice) => <li key={invoice.id}>{invoice.clientName ?? "Client not selected"} · {invoice.number ?? "Draft"}: {paymentEligibility(invoice)}</li>)}</ul></div> : null}
      {eligible.length ? <>
        <div className="batch-payment-shared"><Field label="Payment date" type="date" required value={paidOn} error={errors.paidOn} onChange={(event) => setPaidOn(event.target.value)} /><Field label="Payment reference" required value={reference} error={errors.reference} maxLength={200} onChange={(event) => setReference(event.target.value)} /></div>
        <div className="batch-payment-allocations">{eligible.map((invoice) => <div className="batch-payment-row" key={invoice.id}>
          <div><strong>{invoice.clientName ?? "Client not selected"}</strong><small>{invoice.number ?? "Invoice"}</small></div>
          <div><small>Issued {displayDate(invoice.issueDate)}</small><small>Due {displayDate(invoice.dueDate)}</small></div>
          <div><small>Outstanding</small><strong>{formatMoney(invoiceOutstandingMinor(invoice))}</strong></div>
          <Field label={`Payment amount for ${invoice.number ?? invoice.clientName ?? "invoice"}`} inputMode="decimal" value={amounts[invoice.id] ?? ""} error={errors[invoice.id]} onChange={(event) => setAmounts((current) => ({ ...current, [invoice.id]: event.target.value }))} />
        </div>)}</div>
      </> : <p>No selected invoices are eligible for payment.</p>}
      {error ? error === SUBSCRIPTION_REQUIRED_MESSAGE ? <RequestNotice title="Payments not recorded" message={error} /> : <p role="alert">{error}</p> : null}
      <div className="modal-actions"><Button variant="quiet" disabled={busy} onClick={onClose}>Cancel</Button><Button loading={busy} disabled={!eligible.length} onClick={() => void submit()}>Record {eligible.length} payment{eligible.length === 1 ? "" : "s"}</Button></div>
    </div>
  </Modal>;
}
